import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import SafeIcon from '../common/SafeIcon';
import * as LuIcons from 'react-icons/lu';

const { LuCalendar, LuCar, LuAnchor, LuTruck, LuActivity, LuCaravan, LuDisc, LuPhone } = LuIcons;

// Custom System Control Icon in Lucide style
const SystemControlIcon = ({ size = 24, className = "" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="1.5" 
    strokeLinecap="round" 
    strokeLinejoin="round"
    className={className}
  >
    {/* Gear Body (Right dominant) */}
    <path d="M12 2v2m0 16v2m7.07-17.07l-1.41 1.41m0 11.32l1.41 1.41M22 12h-2" />
    <path d="M12 16a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />
    <path d="M17.66 7.76c.5.5.9 1.1 1.1 1.8l1.8.5c.1.5.2 1 .2 1.5s-.1 1-.2 1.5l-1.8.5c-.2.7-.5 1.3-1.1 1.8" />
    
    {/* Automation / Integration Bars (Left merging) */}
    <path d="M2 10h8" />
    <path d="M2 14h8" />
    <path d="M4 7l3 3" />
    <path d="M4 17l3-3" />
  </svg>
);

const HeroSection = () => {
  const { t } = useTranslation();

  const orbitIcons = [
    { icon: LuCar, color: '#8377BE', label: t('services.car.title') },
    { icon: LuAnchor, color: '#DDDBE6', label: t('services.marine.title') },
    { icon: LuTruck, color: '#50487A', label: t('services.motorhome.title') },
    { icon: LuCaravan, color: '#2dd4bf', label: t('services.trailer.title') }
  ];

  return (
    <section id="hero" className="section-container min-h-[90vh]">
      <div className="hero-background">
        <div className="overlay-gradient"></div>
      </div>
      
      <div className="hero-grid">
        <motion.div 
          initial={{ opacity: 0, x: -50 }} 
          whileInView={{ opacity: 1, x: 0 }} 
          transition={{ duration: 0.8 }}
        >
          <span className="premium-badge">Strömstad's Premier Service Center</span>
          <h1 className="hero-title">
            {t('hero.title')} <br/>
            <span className="gradient-text">{t('hero.titleGradient')}</span>
          </h1>
          <p className="hero-subtitle">{t('hero.subtitle')}</p>
          <div className="hero-actions">
            <a href="#contact" className="gradient-button">
              {t('hero.bookBtn')} <SafeIcon icon={LuCalendar} />
            </a>
            <a href="tel:+46722435249" className="outlined-button">
              <SafeIcon icon={LuPhone} /> {t('hero.callBtn')}
            </a>
          </div>
        </motion.div>

        <motion.div 
          className="hero-visual"
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1 }}
        >
          <div className="interactive-scene">
            {/* --- GROUND SYSTEM --- */}
            <div className="car-hologram-container">
              <div className="ground-grid-system">
                <div className="grid-lines"></div>
                <motion.div 
                  className="data-pulse-ring"
                  animate={{ scale: [1, 2.5], opacity: [0.3, 0] }}
                  transition={{ duration: 3, repeat: Infinity }}
                />
              </div>
            </div>

            {/* --- CENTRAL SPINNING WHEEL CORE --- */}
            <motion.div 
              className="service-core-assembly"
              animate={{ y: [0, -20, 0] }}
              transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            >
              <div className="core-unit-large">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
                  className="wheel-rim"
                >
                  <SafeIcon icon={LuDisc} size={110} />
                  <div className="wheel-spokes"></div>
                </motion.div>
                <div className="core-glow-field"></div>
                
                {/* REPLACED: Custom Automation Icon replacing magnetic-rings */}
                <motion.div 
                  className="automation-icon-overlay"
                  animate={{ rotate: -360 }}
                  transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                >
                  <SystemControlIcon size={240} className="icon-svg" />
                </motion.div>
              </div>

              {/* Orbiting Tech Nodes */}
              <div className="orbit-field-expanded">
                {orbitIcons.map((item, i) => (
                  <motion.div
                    key={i}
                    className="node-orbit-wrapper"
                    animate={{ rotate: 360 }}
                    transition={{ 
                      duration: 35, 
                      repeat: Infinity, 
                      ease: "linear",
                      delay: i * -8.75
                    }}
                  >
                    <motion.div 
                      className="tech-node"
                      animate={{ rotate: -360 }}
                      transition={{ duration: 35, repeat: Infinity, ease: "linear", delay: i * -8.75 }}
                    >
                      <div className="node-icon-box-large" style={{ color: 'rgb(195, 195, 243)' }}>
                        <SafeIcon icon={item.icon} size={36} />
                      </div>
                      <div className="node-label-premium">{item.label}</div>
                    </motion.div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>

      <style jsx>{`
        #hero { position: relative; overflow: hidden; }
        
        .hero-background {
          position: absolute;
          top: 0; left: 0; width: 100%; height: 100%;
          background-image: url('https://media-manager-c.questera.ai/greta-media/25fe57c277fc4d49baa2815a1646d92e367cb2ce89a7d2c278a4f8202228d92c658a999133b0fffe7649687a42276768/images/aW1hZ2UvanBlZw==/c1f480bf32da662a7536b0c299fc39c9.jpg');
          background-size: cover;
          background-position: center 30%;
          z-index: 0;
          opacity: 0.35;
        }

        .overlay-gradient {
          position: absolute;
          inset: 0;
          background: radial-gradient(circle at 75% 50%, transparent 0%, #07070E 100%),
                      linear-gradient(to right, #07070E 0%, transparent 50%, #07070E 100%);
          opacity: 0.9;
        }

        .hero-grid {
          position: relative;
          z-index: 1;
          max-width: 1440px;
          margin: auto;
          display: grid;
          grid-template-columns: 1fr 1.1fr;
          gap: 4rem;
          align-items: center;
          padding: 0 4rem;
        }

        .hero-title { font-size: 4.8rem; font-weight: 800; line-height: 1.05; margin-bottom: 1.5rem; letter-spacing: -0.02em; }
        .hero-subtitle { font-size: 1.25rem; color: #DDDBE6; margin-bottom: 3.5rem; max-width: 550px; opacity: 0.7; line-height: 1.6; }
        .hero-actions { display: flex; gap: 1.5rem; flex-wrap: wrap; }

        .outlined-button {
          border: 2px solid rgba(131, 119, 190, 0.4);
          background: transparent;
          color: #DDDBE6;
          padding: 1rem 2rem;
          border-radius: 14px;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
          gap: 0.75rem;
        }

        .outlined-button:hover {
          border-color: #8377BE;
          color: white;
          background: rgba(131, 119, 190, 0.1);
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(131, 119, 190, 0.15);
        }

        .interactive-scene {
          position: relative;
          height: 700px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .ground-grid-system {
          position: absolute;
          bottom: -20px;
          left: 50%;
          transform: translateX(-50%) rotateX(70deg);
          width: 800px;
          height: 300px;
          z-index: -1;
        }

        .grid-lines {
          width: 100%;
          height: 100%;
          background-image: 
            linear-gradient(rgba(131, 119, 190, 0.15) 1.5px, transparent 1.5px),
            linear-gradient(90deg, rgba(131, 119, 190, 0.15) 1.5px, transparent 1.5px);
          background-size: 40px 40px;
          mask-image: radial-gradient(circle at 50% 50%, black, transparent 80%);
        }

        .core-unit-large {
          width: 180px;
          height: 180px;
          background: radial-gradient(circle, #17066E 0%, #07070E 100%);
          border: 3px solid #8377BE;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #8377BE;
          box-shadow: 0 0 60px rgba(131, 119, 190, 0.5), inset 0 0 30px rgba(131, 119, 190, 0.2);
          position: relative;
          z-index: 5;
        }

        .wheel-rim {
          display: flex;
          align-items: center;
          justify-content: center;
          filter: drop-shadow(0 0 15px rgba(131, 119, 190, 0.8));
          z-index: 2;
        }

        .automation-icon-overlay {
          position: absolute;
          inset: -40px;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0.15;
          pointer-events: none;
          z-index: 1;
        }

        .icon-svg {
          color: #8377BE;
          filter: drop-shadow(0 0 10px rgba(131, 119, 190, 0.3));
        }

        .orbit-field-expanded {
          position: absolute;
          width: 480px;
          height: 480px;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        }

        .node-orbit-wrapper {
          position: absolute;
          inset: 0;
        }

        .tech-node {
          position: absolute;
          top: -40px;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1rem;
        }

        .node-icon-box-large {
          width: 82px;
          height: 82px;
          background: #07070E;
          border: 3px solid;
          border-radius: 22px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          box-shadow: 0 15px 35px rgba(0,0,0,0.7);
          transition: 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }

        .node-label-premium {
          font-size: 0.85rem;
          color: white;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          background: rgba(131, 119, 190, 0.15);
          padding: 6px 14px;
          border-radius: 8px;
          border: 1px solid rgba(131, 119, 190, 0.3);
          backdrop-filter: blur(8px);
        }

        .diagnostic-status-v2 {
          position: absolute;
          bottom: 0;
          display: flex;
          align-items: center;
          gap: 1rem;
          background: rgba(7, 7, 14, 0.8);
          padding: 0.8rem 1.8rem;
          border-radius: 12px;
          border: 1px solid rgba(131, 119, 190, 0.2);
          font-family: 'JetBrains Mono', monospace;
          font-size: 0.75rem;
          color: #8377BE;
        }

        .status-dot {
          width: 8px; height: 8px; background: #2dd4bf; border-radius: 50%;
          box-shadow: 0 0 10px #2dd4bf; animation: pulse-status 2s infinite;
        }

        @keyframes pulse-status {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.2); }
        }

        @media (max-width: 1200px) {
          .hero-grid { grid-template-columns: 1fr; text-align: center; padding: 2rem; }
          .hero-title { font-size: 3.5rem; }
          .hero-actions { justify-content: center; }
          .interactive-scene { height: 560px; }
          .orbit-field-expanded { width: 380px; height: 380px; }
          .core-unit-large { width: 140px; height: 140px; }
          .node-icon-box-large { width: 64px; height: 64px; }
          .automation-icon-overlay { inset: -20px; }
        }

        @media (max-width: 768px) {
          .hero-grid { padding: 1rem; gap: 2rem; }
          .hero-title { font-size: 2.6rem; }
          .hero-subtitle { font-size: 1rem; margin-bottom: 2rem; }
          .interactive-scene { height: 420px; }
          .orbit-field-expanded { width: 300px; height: 300px; }
          .core-unit-large { width: 110px; height: 110px; }
          .node-icon-box-large { width: 52px; height: 52px; border-radius: 14px; }
          .automation-icon-overlay { inset: -10px; }
          .tech-node { top: -28px; }
          .node-label-premium { font-size: 0.7rem; padding: 4px 8px; }
          .hero-actions { gap: 1rem; }
          .gradient-button, .outlined-button { padding: 0.8rem 1.4rem; font-size: 0.9rem; }
        }

        @media (max-width: 480px) {
          .hero-title { font-size: 2rem; }
          .hero-subtitle { font-size: 0.9rem; }
          .interactive-scene { height: 340px; }
          .orbit-field-expanded { width: 240px; height: 240px; }
          .core-unit-large { width: 88px; height: 88px; }
          .node-icon-box-large { width: 42px; height: 42px; border-radius: 10px; }
          .wheel-rim svg { width: 72px !important; height: 72px !important; }
          .hero-actions { flex-direction: column; align-items: center; }
          .gradient-button, .outlined-button { width: 100%; justify-content: center; }
        }
      `}</style>
    </section>
  );
};

export default HeroSection;